import type {
  HoraCertaQuarantineDock,
  HoraCertaQuarantineRecord,
} from 'contracts/HoraCertaImport';
import { Button } from 'components/ui/button';
import type { RowSelection, SavedOrder, SlotOption } from '../../types';
import { QuarantineRow } from '../QuarantineRow';

interface QuarantineTableProps {
  records: HoraCertaQuarantineRecord[];
  isLoading: boolean;
  selection: Record<number, RowSelection>;
  savedOrders: Record<number, SavedOrder>;
  docksByRecord: Record<number, HoraCertaQuarantineDock[]>;
  loadingDocks: Record<number, boolean>;
  rowErrors: Record<number, string>;
  savingId: number | null;
  batchCount: number;
  isBatchSaving: boolean;
  getSlotOptions: (record: HoraCertaQuarantineRecord) => SlotOption[];
  getStandardDuration: (record: HoraCertaQuarantineRecord) => number | null;
  onCargoTypeChange: (recordId: number, cargoTypeId: number) => void;
  onSlotChange: (recordId: number, slot: string) => void;
  onSave: (record: HoraCertaQuarantineRecord) => void;
  onSaveBatch: () => void;
  onDelete: (record: HoraCertaQuarantineRecord) => void;
  onReload: () => void;
  onExpirePast?: () => void;
  isExpiring?: boolean;
}

export const QuarantineTable = ({
  records,
  isLoading,
  selection,
  savedOrders,
  docksByRecord,
  loadingDocks,
  rowErrors,
  savingId,
  batchCount,
  isBatchSaving,
  getSlotOptions,
  getStandardDuration,
  onCargoTypeChange,
  onSlotChange,
  onSave,
  onSaveBatch,
  onDelete,
  onReload,
  onExpirePast,
  isExpiring,
}: QuarantineTableProps) => {
  const savedCount = Object.keys(savedOrders).length;
  const pendingCount = records.length - savedCount;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <span className="text-sm font-GilroySemibold text-slate-700">
          Ordens prontas para atribuição
        </span>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-orange-50 text-[#EE7C2C]">
          {pendingCount} pendente(s) de {records.length}
        </span>
      </div>

      {isLoading ? (
        <p className="p-6 text-sm text-slate-500">Carregando...</p>
      ) : records.length === 0 ? (
        <p className="p-6 text-sm text-slate-500">
          Nenhum registro pendente na quarentena.
        </p>
      ) : (
        <div className="max-h-[65vh] overflow-auto">
          <table className="min-w-max text-left text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase">
                <th
                  className="sticky left-0 z-20 bg-slate-50 py-3 px-6"
                  style={{
                    boxShadow: '4px 0 8px -6px rgba(15, 23, 42, 0.14)',
                  }}
                >
                  Agendamento
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 -right-2 w-2"
                    style={{
                      background:
                        'linear-gradient(to right, rgba(15, 23, 42, 0.09), transparent)',
                    }}
                  />
                </th>
                <th className="py-3 px-6">Data agendamento</th>
                <th className="py-3 px-6">Hr. Inicial</th>
                <th className="py-3 px-6">Armazém</th>
                <th className="py-3 px-6">Transportadora</th>
                <th className="py-3 px-6">Tomador</th>
                <th className="py-3 px-6">Tipo operação</th>
                <th className="py-3 px-6">Operação</th>
                <th className="py-3 px-6">Peso (Kg)</th>
                <th className="py-3 px-6">Motorista</th>
                <th className="py-3 px-6">CPF</th>
                <th className="py-3 px-6">Celular</th>
                <th className="py-3 px-6">Placa</th>
                <th className="py-3 px-6">Tipo veículo</th>
                <th className="py-3 px-6">Observação</th>
                <th className="py-3 px-6">Tipo de Carga</th>
                <th className="py-3 px-6">Tempo carregamento WC</th>
                <th className="py-3 px-6">Doca</th>
                <th className="py-3 px-6 text-center">Salvar</th>
                <th className="py-3 px-6 text-center">Excluir</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {records.map((record) => (
                <QuarantineRow
                  key={record.id}
                  record={record}
                  selection={selection[record.id]}
                  saved={savedOrders[record.id]}
                  docks={docksByRecord[record.id] ?? []}
                  isLoadingDocks={Boolean(loadingDocks[record.id])}
                  isSaving={savingId === record.id}
                  isBusy={savingId !== null || isBatchSaving}
                  error={rowErrors[record.id]}
                  standardDuration={getStandardDuration(record)}
                  cargoOptions={record.cargoTypes}
                  slotOptions={getSlotOptions(record)}
                  onCargoTypeChange={onCargoTypeChange}
                  onSlotChange={onSlotChange}
                  onSave={onSave}
                  onDelete={onDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-slate-500">
          Cada ordem é criada no Webcol ao clicar no ícone de salvar.
        </p>
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            disabled={
              batchCount === 0 || isLoading || isBatchSaving || savingId !== null
            }
            onClick={onSaveBatch}
          >
            {isBatchSaving ? 'Salvando...' : `Salvar em lote (${batchCount})`}
          </Button>
          {onExpirePast && (
            <Button
              type="button"
              variant="outline"
              disabled={isLoading || isExpiring || records.length === 0}
              onClick={onExpirePast}
            >
              {isExpiring ? 'Limpando...' : 'Limpar antigos'}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            disabled={isLoading}
            onClick={onReload}
          >
            Recarregar
          </Button>
        </div>
      </div>
    </div>
  );
};
